import React, { useState, useEffect, useCallback } from "react";
import { getSurvaySupport } from "../Services/contactSupportService";
import { customStyles, surveySupportOptions } from "../Router/constant";
import Select from "react-select";
import Skeleton from "../Components/SkeletonLoader";
import ViewSurveyModal from "../Components/ViewSurveyModal";

const getSurveyRecords = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  if (Array.isArray(payload.surveys)) {
    return payload.surveys;
  }

  if (Array.isArray(payload.contacts)) {
    return payload.contacts;
  }

  if (Array.isArray(payload.survey)) {
    return payload.survey;
  }

  if (Array.isArray(payload.records)) {
    return payload.records;
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  if (Array.isArray(payload.docs)) {
    return payload.docs;
  }

  if (Array.isArray(payload.items)) {
    return payload.items;
  }

  if (Array.isArray(payload.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload.data)) {
    return payload.data;
  }

  if (payload.data && typeof payload.data === "object") {
    return getSurveyRecords(payload.data);
  }

  if (payload._id) {
    return [payload];
  }

  return [];
};

const getFirstNumber = (sources, keys) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") {
      continue;
    }

    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null) {
        const numberValue = Number(source[key]);

        if (Number.isFinite(numberValue)) {
          return numberValue;
        }
      }
    }
  }

  return null;
};

const logisticsTypeLabels = {
  1: "Air freight",
  2: "Sea freight",
  3: "Rail freight",
  4: "Trucking",
  5: "Warehouse",
};

const getSurveyCount = (responseBody, fallback) => {
  const data = responseBody?.data;
  const sources = [
    data,
    data?.pagination,
    data?.meta,
    data?.pageInfo,
    responseBody,
    responseBody?.pagination,
    responseBody?.meta,
    responseBody?.pageInfo,
  ];

  return (
    getFirstNumber(sources, [
      "total",
      "count",
      "totalCount",
      "total_count",
      "totalRecords",
      "total_records",
      "totalItems",
      "total_items",
      "totalDocs",
      "total_docs",
      "totalSurvey",
      "totalSurveys",
    ]) ?? fallback
  );
};

const getSurveyTotalPages = (responseBody, count, limit) => {
  const data = responseBody?.data;
  const sources = [
    data,
    data?.pagination,
    data?.meta,
    data?.pageInfo,
    responseBody,
    responseBody?.pagination,
    responseBody?.meta,
    responseBody?.pageInfo,
  ];

  return (
    getFirstNumber(sources, [
      "totalPages",
      "total_pages",
      "totalPage",
      "total_page",
      "pages",
      "pageCount",
      "page_count",
    ]) ?? Math.ceil(count / limit)
  );
};

export default function Survey() {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [logisticsType, setLogisticsType] = useState("");
  const [contactSupportData, setContactSupportData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchSupportData = useCallback(async () => {
    setLoading(true);

    try {
      const contactResp = await getSurvaySupport({
        page,
        limit,
        search,
        logisticsType,
      });
      const responseBody = contactResp.data;
      const responseData = responseBody?.data;
      const data = getSurveyRecords(responseData ?? responseBody);
      const totalRecords = getSurveyCount(responseBody, data.length);
      setContactSupportData(data);
      setTotalPages(getSurveyTotalPages(responseBody, totalRecords, limit));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, logisticsType]);

  useEffect(() => {
    fetchSupportData();
  }, [fetchSupportData]);

  const handleView = (contact) => {
    setSelectedContact(contact);
    setOpenModal(true);
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 bookingDetailsMain">
            <div className="bookingDetailsOuter">
              <div className="bookingDetailsAlign">
                <h6 className="bookingDetailsTitle p-0">
                  Survey contact details
                </h6>
                <div className="bookingDetailsOuterRight">
                  <div>
                    <input
                      type="text"
                      placeholder="Search"
                      className="searchSurvay"
                      value={search}
                      onChange={(e) => {
                        setPage(1);
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                  <div className="customSelectWrapper">
                    <Select
                      className="searchInputSelect"
                      options={surveySupportOptions}
                      value={surveySupportOptions.find(
                        (opt) => opt.value === logisticsType,
                      )}
                      onChange={(selected) => {
                        setPage(1);
                        setLogisticsType(selected?.value || "");
                      }}
                      styles={customStyles}
                    />
                  </div>
                </div>
              </div>
              <div className="TableOverFlow">
                <div className="bookingContent bookingDetailsHeader">
                  <div className="m-0 userNameWidth">Company name</div>
                  <div className="m-0 userNameWidth">Name</div>
                  <div className="m-0 contactEmailWidth">Email</div>
                  <div className="m-0 userNameWidth">Phone number</div>
                  {/* <div className="m-0 userNameWidth">Description</div> */}
                  <div className="m-0 userNameWidth">Date</div>
                  <div className="m-0 userNameWidth">Logistics type</div>
                  <div className="m-0 userNameWidth">Action</div>
                </div>

                <div>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <div
                        className="bookingContent bookingContentData"
                        key={i}
                      >
                        <div className="m-0 userNameWidth">
                          <Skeleton width={100} />
                        </div>
                        <div className="m-0 userNameWidth">
                          <Skeleton width={100} />
                        </div>
                        <div className="m-0 contactEmailWidth">
                          <Skeleton width={140} />
                        </div>
                        <div className="m-0 userNameWidth">
                          <Skeleton width={100} />
                        </div>
                        <div className="m-0 userNameWidth">
                          <Skeleton width={120} />
                        </div>
                        {/* <div className="m-0 userNameWidth">
                          <Skeleton width={90} />
                        </div> */}
                        <div className="m-0 userNameWidth">
                          <Skeleton width={60} />
                        </div>
                        <div className="m-0 userNameWidth">
                          <Skeleton width={60} />
                        </div>
                      </div>
                    ))
                  ) : contactSupportData.length > 0 ? (
                    contactSupportData.map((contact) => (
                      <div
                        className="bookingContent bookingContentData"
                        key={contact._id}
                      >
                        <div className="m-0 userNameWidth">
                          {contact.companyName || "--"}
                        </div>
                        <div className="m-0 userNameWidth">
                          {contact.fullName || "--"}
                        </div>
                        <div className="m-0 contactEmailWidth">
                          {contact.email}
                        </div>
                        <div className="m-0 userNameWidth">
                          {contact.phoneNumber}
                        </div>
                        {/* <div className="m-0 userNameWidth limitLineCss">
                          {contact.message}
                        </div> */}
                        <div className="m-0 userNameWidth">
                          {new Date(contact.createdAt).toLocaleDateString()}
                        </div>
                        <div className="m-0 userNameWidth">
                          {logisticsTypeLabels[contact?.logisticsType] && (
                            <div className="BadgeDesign">
                              {logisticsTypeLabels[contact.logisticsType]}
                            </div>
                          )}
                        </div>
                        <div
                          className="m-0 userNameWidth"
                          onClick={() => handleView(contact)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="viewActionBtn">View</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="noOrders">No survey contact details Found</p>
                  )}
                </div>
              </div>

              {totalPages > 1 && (
                <div className="paginationSection">
                  <p
                    onClick={() => page > 1 && setPage(page - 1)}
                    style={{
                      opacity: page > 1 ? 1 : 0.5,
                      pointerEvents: page > 1 ? "auto" : "none",
                      cursor: page > 1 ? "pointer" : "default",
                    }}
                  >
                    <img src="/assets/img/arrow-left.svg" alt="Previous" />
                  </p>

                  {[...Array(totalPages)].map((_, index) => (
                    <p
                      key={index}
                      onClick={() => setPage(index + 1)}
                      style={{
                        fontWeight: page === index + 1 ? "bold" : "normal",
                        cursor: "pointer",
                      }}
                    >
                      {index + 1}
                    </p>
                  ))}

                  <p
                    onClick={() => page < totalPages && setPage(page + 1)}
                    style={{
                      opacity: page < totalPages ? 1 : 0.5,
                      pointerEvents: page < totalPages ? "auto" : "none",
                      cursor: page < totalPages ? "pointer" : "default",
                    }}
                  >
                    <img src="/assets/img/arrow-right.svg" alt="Next" />
                  </p>
                </div>
              )}

              {openModal && (
                <ViewSurveyModal
                  contact={selectedContact}
                  onClose={() => setOpenModal(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
